import React from 'react'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  Divider,
} from '@mui/material'

const SKILL_CATEGORIES = [
  { value: 'mindfulness', label: 'Mindfulness' },
  { value: 'relaxation', label: 'Relaxation' },
  { value: 'cognitive', label: 'Cognitive' },
  { value: 'behavioral', label: 'Behavioral' },
]

/**
 * Skill Building track type configuration component
 * Rendered within the Agent Config tab for skill building tracks
 *
 * @param {Object} config - The type_config object
 * @param {Function} onChange - Callback when config changes
 * @param {string} trackCode - The track code (for context)
 */
function SkillBuildingConfig({ config = {}, onChange, trackCode }) {
  const updateConfig = (field, value) => {
    onChange({
      ...config,
      [field]: value,
    })
  }

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom fontWeight="medium">
        Skill Building Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Configure the skill category and practice settings for this track.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        {/* Skill Category */}
        <FormControl fullWidth margin="normal">
          <InputLabel>Skill Category</InputLabel>
          <Select
            value={config.skill_category || ''}
            label="Skill Category"
            onChange={(e) => updateConfig('skill_category', e.target.value)}
          >
            <MenuItem value="">
              <em>Select a category...</em>
            </MenuItem>
            {SKILL_CATEGORIES.map((cat) => (
              <MenuItem key={cat.value} value={cat.value}>
                {cat.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Guided Audio Reference */}
        <TextField
          fullWidth
          label="Guided Audio Reference"
          value={config.guided_audio_ref || ''}
          onChange={(e) => updateConfig('guided_audio_ref', e.target.value)}
          helperText="Reference to guided audio content (optional)"
          margin="normal"
          inputProps={{ style: { fontFamily: 'monospace' } }}
        />

        <Divider sx={{ my: 3 }} />

        {/* Practice Settings */}
        <Typography variant="subtitle2" gutterBottom>
          Practice Settings
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <TextField
            label="Practice Duration (minutes)"
            type="number"
            value={config.practice_duration_minutes ?? 5}
            onChange={(e) =>
              updateConfig('practice_duration_minutes', parseInt(e.target.value, 10) || 5)
            }
            helperText="Target duration for each practice session"
            inputProps={{ min: 1, max: 60 }}
            sx={{ width: 220 }}
          />

          <TextField
            label="Mastery Threshold"
            type="number"
            value={config.mastery_threshold ?? 3}
            onChange={(e) =>
              updateConfig('mastery_threshold', parseInt(e.target.value, 10) || 3)
            }
            helperText="Practices required for mastery"
            inputProps={{ min: 1, max: 20 }}
            sx={{ width: 200 }}
          />
        </Box>
      </Paper>
    </Box>
  )
}

export default SkillBuildingConfig
